import { Box, CircularProgress, Link } from '@mui/material';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../instant';
import { APP_SETTINGS_ID, EVENTS_LIST_ROUTE } from '../constants';
import { buildWelcomeDocViewUrl } from '../utils/welcomeDocUrl';
import WelcomeEmptyState from '../components/WelcomeEmptyState';
import GlobalHeader from '../components/GlobalHeader';
import DefaultLayout from '../layouts/DefaultLayout';
import { usePermissions } from '../hooks/usePermissions';
import WelcomeDocHtmlPanel from '../components/WelcomeDocHtmlPanel';

export default function InfoScreen() {
    const navigate = useNavigate();
    const { isAdmin, isLoading: permissionsLoading } = usePermissions();
    const { user, isLoading: authLoading } = db.useAuth();

    const { data, isLoading: settingsLoading } = db.useQuery({
        appSettings: {
            $: { where: { id: APP_SETTINGS_ID } },
        },
    });

    const settings = data?.appSettings?.[0];
    const docId = settings?.welcomeDocId ?? null;
    const tabId = settings?.welcomeTabId ?? null;
    const welcomeRef = { docId, tabId };
    const viewUrl = buildWelcomeDocViewUrl(welcomeRef);

    const ready = !permissionsLoading && !settingsLoading;

    useEffect(() => {
        if (ready && docId == null && !isAdmin) {
            navigate(EVENTS_LIST_ROUTE, { replace: true });
        }
    }, [ready, docId, isAdmin, navigate]);

    if (!ready) {
        return (
            <DefaultLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, p: 4 }}>
                    <CircularProgress />
                </Box>
            </DefaultLayout>
        );
    }

    if (docId == null && !isAdmin) {
        return null;
    }

    return (
        <DefaultLayout>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    width: '100%',
                    padding: 2,
                    boxSizing: 'border-box',
                    minHeight: 0,
                }}
            >
                <GlobalHeader title="Info" />
                {viewUrl && (
                    <Box sx={{ marginBottom: 2, textAlign: 'center' }}>
                        <Link href={viewUrl} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.875rem' }}>
                            Open in Google Docs
                        </Link>
                    </Box>
                )}
                <Box
                    sx={{
                        flex: 1,
                        width: '100%',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {docId ? (
                        <WelcomeDocHtmlPanel
                            docId={docId}
                            tabId={tabId}
                            token={user?.refresh_token}
                            authReady={!authLoading}
                            viewOnly={false}
                            viewUrl={viewUrl}
                        />
                    ) : (
                        <WelcomeEmptyState
                            title="No info document set"
                            hint="Admins can set the global info document in Settings."
                            editLink={{ to: '/settings', label: 'Go to Settings' }}
                        />
                    )}
                </Box>
            </Box>
        </DefaultLayout>
    );
}
